import { formatUnits, parseUnits } from 'viem'

/**
 * Display helpers. Nothing here is ever fed back into a request: the amount that goes to
 * `/quote` comes from `parseAmount` below, straight off what the user typed, and every
 * amount that comes back is carried as a string and only formatted on the way to the screen.
 * A number that has been through `toFixed` must never become calldata.
 */

/** User input -> raw token units. Null for anything that is not a positive amount. */
export function parseAmount(input: string, decimals: number): bigint | null {
  const trimmed = input.trim()
  if (trimmed.length === 0) return null
  if (!/^\d*\.?\d*$/.test(trimmed)) return null
  const [, fraction = ''] = trimmed.split('.')
  // parseUnits rounds a longer fraction away silently; refuse it instead.
  if (fraction.length > decimals) return null
  try {
    const raw = parseUnits(trimmed, decimals)
    return raw > 0n ? raw : null
  } catch {
    return null
  }
}

/** Raw units -> a readable amount, with the trailing zeros dropped. */
export function formatAmount(raw: bigint | string, decimals: number, significant = 6): string {
  const value = formatUnits(typeof raw === 'bigint' ? raw : BigInt(raw), decimals)
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return value
  if (parsed === 0) return '0'
  if (Math.abs(parsed) < 10 ** -significant) return `<${10 ** -significant}`
  const fixed = parsed.toLocaleString('en-US', { maximumSignificantDigits: significant })
  return fixed
}

/** Raw units -> a full-precision string, for a tooltip or a copy target. */
export function formatExact(raw: bigint | string, decimals: number): string {
  return formatUnits(typeof raw === 'bigint' ? raw : BigInt(raw), decimals)
}

export function formatBps(bps: number): string {
  if (!Number.isFinite(bps)) return '—'
  return `${(bps / 100).toFixed(bps < 10 ? 3 : 2)}%`
}

export function shortAddress(address: string): string {
  return `${address.slice(0, 6)}…${address.slice(-4)}`
}

export function formatMs(ms: number): string {
  return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(2)} s`
}

/**
 * The exchange rate implied by a quote, as one line of text.
 *
 * Derived from the quoted amounts and nothing else. There is no independent price here and
 * there must not be: a rate computed from pool reserves in the browser would be the local
 * AMM math that rule 1 exists to forbid, and on this chain it would be wrong by up to 6x
 * on the fee alone.
 */
export function formatRate(
  amountIn: string,
  amountOut: string,
  decimalsIn: number,
  decimalsOut: number,
  symbolIn: string,
  symbolOut: string
): string {
  const input = Number(formatUnits(BigInt(amountIn), decimalsIn))
  const output = Number(formatUnits(BigInt(amountOut), decimalsOut))
  if (!input || !Number.isFinite(input) || !Number.isFinite(output)) return '—'
  const rate = output / input
  return `1 ${symbolIn} = ${rate.toLocaleString('en-US', { maximumSignificantDigits: 6 })} ${symbolOut}`
}

/** Minimum received after slippage, computed the same way the encoder does. */
export function applySlippage(amountOut: bigint, slippageBps: number): bigint {
  return (amountOut * BigInt(10_000 - slippageBps)) / 10_000n
}
